import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

/**
 * FIXED Sankey Diagram Component - Corrected Flow Visualization
 * 
 * FIXES IMPLEMENTED:
 * 1. Factory waste heat flows directly to LTES (no production loss)
 * 2. Separate turbine heat and electricity outputs 
 * 3. An-værk = ONLY operational solar + grid (no arbitrage)
 * 4. Production loss (3%) applied at electric heater level
 * 5. Proper flow structure: Solar/Grid → Electric Heater → HTES/LTES
 * 6. LTES → Ab-værk (no 5% LTES loss) → Grid Loss (10%) → Net Delivery
 */

const SankeyDiagramSimple = ({ 
  hourlyData = [], 
  currentHour = 0, 
  stats = {},
  cumulativeFlows = {},
  config = {}
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  
  // Initialize chart
  useEffect(() => {
    if (!chartRef.current) return;
    
    chartInstance.current = echarts.init(chartRef.current);
    
    return () => {
      if (chartInstance.current) {
        chartInstance.current.dispose();
      }
    };
  }, []);
  
  // Update chart with comprehensive node and link structure
  useEffect(() => {
    if (!chartInstance.current) return;
    
    // Define all nodes with proper categorization - FIXED STRUCTURE
    const nodes = [
      // Input Sources
      { name: 'Grid Electricity', itemStyle: { color: '#E74C3C' }},
      { name: 'Solar Energy', itemStyle: { color: '#F39C12' }},
      { name: 'Factory Waste Heat', itemStyle: { color: '#27AE60' }}, // FIXED: Clearly labeled as factory waste heat
      { name: 'Initial HTES', itemStyle: { color: '#95E1D3' }},
      { name: 'Initial LTES', itemStyle: { color: '#95E1D3' }},
      
      // Processing
      { name: 'Electric Heater', itemStyle: { color: '#3498DB' }}, // FIXED: More descriptive name
      { name: 'HTES', itemStyle: { color: '#E67E22' }},
      { name: 'LTES', itemStyle: { color: '#D35400' }},
      { name: 'Battery', itemStyle: { color: '#7F8C8D' }},
      { name: 'Turbine', itemStyle: { color: '#8E44AD' }},
      
      // FIXED: Separate turbine outputs
      { name: 'Turbine Heat', itemStyle: { color: '#9B59B6' }}, // NEW: Separate turbine heat output
      { name: 'Turbine Electricity', itemStyle: { color: '#663399' }}, // NEW: Separate turbine electricity output
      
      // Production Chain
      { name: 'An-værk', itemStyle: { color: '#C0392B' }},
      { name: 'Ab-værk', itemStyle: { color: '#A93226' }},
      
      // Outputs
      { name: 'Customer Delivery', itemStyle: { color: '#27AE60' }},
      { name: 'Grid Sales', itemStyle: { color: '#2ECC71' }},
      { name: 'Production Loss', itemStyle: { color: '#95A5A6' }},
      { name: 'Grid Loss', itemStyle: { color: '#7F8C8D' }},
      { name: 'Battery Loss', itemStyle: { color: '#BDC3C7' }},
      { name: 'Thermal Loss', itemStyle: { color: '#BDC3C7' }},
      { name: 'Unmet Demand', itemStyle: { color: '#C0392B' }}
    ];
    
    // Build links from cumulative flows and stats
    const links = [];
    
    // Helper to add link if value exists
    const addLink = (source, target, value, color = null) => {
      if (value > 0.01) { // Lower threshold to catch more flows
        const link = {
          source,
          target,
          value: Math.round(value * 100) / 100
        };
        if (color) {
          link.lineStyle = { color, opacity: 0.6 };
        }
        links.push(link);
      }
    };
    
    // FIXED: Parse all hourly data to build corrected flow map
    const flowMap = {
      // FIX: Use actual stats values for correct flow mapping
      // Grid flows - operational goes to heater, arbitrage ONLY to battery
      'Grid->Electric Heater': stats.operationalGridPurchased || 0,
      'Grid->Battery': stats.arbitrageGridPurchased || 0,
      // Solar flows - operational goes to heater, arbitrage to battery if any
      'Solar->Electric Heater': stats.operationalSolarPurchased || 0, 
      'Solar->Battery': stats.arbitrageSolarPurchased || 0,
      
      // FIXED: Factory waste heat goes directly to LTES (no production loss)
      'Factory Waste Heat->LTES': stats.wasteHeatAccepted || 0,
      
      // Electric heater outputs (with 3% production loss applied)
      'Electric Heater->HTES': 0,
      'Electric Heater->LTES': 0,
      'Electric Heater->Production Loss': 0, // NEW: Track production losses
      
      // HTES to Turbine
      'HTES->Turbine': 0,
      
      // FIXED: Separate turbine outputs
      'Turbine->Turbine Heat': 0, // NEW: Heat output from turbine
      'Turbine->Turbine Electricity': 0, // NEW: Electricity output from turbine
      'Turbine Heat->LTES': 0, // NEW: Turbine heat goes to LTES
      'Turbine Electricity->Grid Sales': 0, // NEW: Will be calculated from actions
      
      // LTES to production
      'LTES->An-værk': stats.anPlantProduction || 0,
      
      // Battery operations
      'Battery->Grid Sales': stats.batteryDischargeAmount || 0,
    };
    
    // Process all hourly actions to build flows for heater outputs
    if (hourlyData && hourlyData.length > 0) {
      hourlyData.forEach(hour => {
        if (hour.actions) {
          hour.actions.forEach(action => {
            // FIXED: Map action types to corrected flows with proper efficiency calculations
            switch(action.type) {
              // Operational Grid purchases -> LTES (with 3% production loss)
              case 'OP_CHARGE_LTES_GRID':
              case 'EMERGENCY_GRID_PURCHASE':
              case 'CHARGE_LTES_NEGATIVE':
                const gridToLTESInput = action.amount || 0;
                flowMap['Electric Heater->LTES'] += gridToLTESInput * 0.97; // 3% production loss
                flowMap['Electric Heater->Production Loss'] += gridToLTESInput * 0.03;
                break;
                
              // Grid purchases -> HTES (with 3% production loss)
              case 'OP_CHARGE_HTES_GRID':
              case 'ARB_CHARGE_HTES_GRID':
              case 'CHARGE_HTES_NEGATIVE':
                const gridToHTESInput = action.amount || 0;
                flowMap['Electric Heater->HTES'] += gridToHTESInput * 0.97; // 3% production loss
                flowMap['Electric Heater->Production Loss'] += gridToHTESInput * 0.03;
                break;
                
              // Solar purchases -> LTES (with 3% production loss)
              case 'OP_CHARGE_LTES_SOLAR':
                const solarToLTESInput = action.amount || 0;
                flowMap['Electric Heater->LTES'] += solarToLTESInput * 0.97;
                flowMap['Electric Heater->Production Loss'] += solarToLTESInput * 0.03;
                break;
                
              // Solar purchases -> HTES (with 3% production loss)
              case 'OP_CHARGE_HTES_SOLAR':
              case 'ARB_CHARGE_HTES_SOLAR':
                const solarToHTESInput = action.amount || 0;
                flowMap['Electric Heater->HTES'] += solarToHTESInput * 0.97;
                flowMap['Electric Heater->Production Loss'] += solarToHTESInput * 0.03;
                break;
                
              // FIXED: Factory waste heat goes directly to LTES (no production loss)
              case 'CHARGE_LTES_WASTE_HEAT':
              case 'CHARGE_LTES_WASTE_HEAT_RECOVERED':
                flowMap['Factory Waste Heat->LTES'] += action.amount || 0; // No loss for factory waste heat
                break;
                
              // FIXED: Turbine operations with separate heat and electricity outputs
              case 'DISCHARGE_HTES_TURBINE':
              case 'DISCHARGE_HTES_TURBINE_FOR_WASTE':
              case 'DISCHARGE_HTES_TURBINE_FOR_WASTE_FORCED':
              case 'DISCHARGE_HTES_TURBINE_FOR_SOLAR':
                const htesInput = action.input || action.amount || 0;
                flowMap['HTES->Turbine'] += htesInput;
                
                // FIXED: Separate turbine outputs
                if (action.heat) {
                  // Use actual heat output
                  flowMap['Turbine->Turbine Heat'] += action.heat;
                  flowMap['Turbine Heat->LTES'] += action.heat;
                } else {
                  // Estimate: ~60% of HTES input becomes heat
                  const estimatedHeat = htesInput * 0.6;
                  flowMap['Turbine->Turbine Heat'] += estimatedHeat;
                  flowMap['Turbine Heat->LTES'] += estimatedHeat;
                }
                
                if (action.electricity) {
                  // Use actual electricity output
                  flowMap['Turbine->Turbine Electricity'] += action.electricity;
                  flowMap['Turbine Electricity->Grid Sales'] += action.electricity;
                } else {
                  // Estimate: ~30% of HTES input becomes electricity
                  const estimatedElectricity = htesInput * 0.3;
                  flowMap['Turbine->Turbine Electricity'] += estimatedElectricity;
                  flowMap['Turbine Electricity->Grid Sales'] += estimatedElectricity;
                }
                break;
                
              // LTES to An-plant (heat delivery) - already counted in stats
              case 'DISCHARGE_LTES_TO_DH':
              case 'DISCHARGE_LTES_EMERGENCY':
                // Already counted in stats.anPlantProduction
                break;
                
              // Battery operations - already counted in stats
              case 'ARB_BATTERY_CHARGE':
              case 'ARB_BATTERY_CHARGE_FORCED':
              case 'ARB_BATTERY_EMERGENCY_CHARGE':
                // Already counted in stats.arbitrageGridPurchased
                break;
                
              case 'ARB_BATTERY_DISCHARGE':
              case 'ARB_BATTERY_DISCHARGE_FORCED':
              case 'ARB_BATTERY_PRE_DISCHARGE':
                // Already counted in stats.batteryDischargeAmount
                break;
                
              default:
                // Other action types not relevant for flow mapping
                break;
            }
          });
        }
      });
    }
    
    // FIXED: Add all flows from corrected flowMap with proper structure
    // Grid flows (FIXED: Only operational grid purchases go to electric heater)
    addLink('Grid Electricity', 'Electric Heater', flowMap['Grid->Electric Heater'], '#E74C3C');
    addLink('Grid Electricity', 'Battery', flowMap['Grid->Battery'], '#E74C3C');
    
    // Solar flows (FIXED: Only operational solar purchases go to electric heater) 
    addLink('Solar Energy', 'Electric Heater', flowMap['Solar->Electric Heater'], '#F39C12');
    addLink('Solar Energy', 'Battery', flowMap['Solar->Battery'], '#F39C12');
    
    // FIXED: Factory waste heat goes directly to LTES (no production loss)
    addLink('Factory Waste Heat', 'LTES', flowMap['Factory Waste Heat->LTES'], '#27AE60');
    
    // Electric heater outputs (FIXED: With production loss tracking)
    addLink('Electric Heater', 'HTES', flowMap['Electric Heater->HTES'], '#3498DB');
    addLink('Electric Heater', 'LTES', flowMap['Electric Heater->LTES'], '#3498DB');
    addLink('Electric Heater', 'Production Loss', flowMap['Electric Heater->Production Loss'], '#95A5A6');
    
    // HTES discharge to turbine
    addLink('HTES', 'Turbine', flowMap['HTES->Turbine'], '#E67E22');
    
    // FIXED: Separate turbine outputs
    addLink('Turbine', 'Turbine Heat', flowMap['Turbine->Turbine Heat'], '#9B59B6');
    addLink('Turbine', 'Turbine Electricity', flowMap['Turbine->Turbine Electricity'], '#663399');
    addLink('Turbine Heat', 'LTES', flowMap['Turbine Heat->LTES'], '#9B59B6');
    addLink('Turbine Electricity', 'Grid Sales', flowMap['Turbine Electricity->Grid Sales'], '#2ECC71');
    
    // LTES to An-værk production (this is the actual heat delivery)
    if (stats.anPlantProduction > 0) {
      addLink('LTES', 'An-værk', stats.anPlantProduction, '#D35400');
    }
    
    // Battery to grid
    addLink('Battery', 'Grid Sales', flowMap['Battery->Grid Sales'], '#2ECC71');
    
    // FIX: Use actual stats values for production chain
    const anProduction = stats.anPlantProduction || 0;
    const abDelivery = stats.abPlantDelivery || 0;
    const netDelivery = stats.netDelivery || 0;
    const productionLoss = stats.productionLosses || 0;
    const gridLoss = stats.gridLosses || 0;
    
    if (anProduction > 0) {
      // An-værk receives from LTES and delivers to Ab-værk
      addLink('An-værk', 'Ab-værk', abDelivery, '#C0392B');
      
      // Show production loss (3%)
      if (productionLoss > 0) {
        addLink('An-værk', 'Production Loss', productionLoss, '#95A5A6');
      }
    }
    
    // Ab-værk to customer with grid loss
    if (abDelivery > 0) {
      addLink('Ab-værk', 'Customer Delivery', netDelivery, '#27AE60');
      
      // Grid loss (10%)
      if (gridLoss > 0) {
        addLink('Ab-værk', 'Grid Loss', gridLoss, '#7F8C8D');
      }
    }
    
    // Battery operations - already handled in flowMap above
    
    // Battery losses if any
    if (stats.batteryLosses > 0) {
      addLink('Battery', 'Battery Loss', stats.batteryLosses, '#BDC3C7');
    }
    
    // Add initial storage if at beginning
    if (currentHour === 0) {
      const initialHTES = (config.htes_modules || 2) * 150 * 0.5; // 50% initial charge
      const initialLTES = (config.ltes_capacity || 205) * 0.5; // 50% initial charge
      
      if (initialHTES > 0) {
        addLink('Initial HTES', 'HTES', initialHTES, '#95E1D3');
      }
      if (initialLTES > 0) {
        addLink('Initial LTES', 'LTES', initialLTES, '#95E1D3');
      }
    }
    
    // Unmet demand if any
    if (stats.unmetDemand > 0) {
      addLink('LTES', 'Unmet Demand', stats.unmetDemand, '#C0392B');
    }
    
    // FIX: Calculate totals using ACTUAL stats values
    // An-værk production is tracked in stats.anPlantProduction
    const anVaerkProduction = stats.anPlantProduction || 0;
    // Input through electric heater (operational only)
    const heaterInput = (stats.operationalGridPurchased || 0) + (stats.operationalSolarPurchased || 0);
    const totalGridInput = (stats.operationalGridPurchased || 0) + (stats.arbitrageGridPurchased || 0);
    const totalSolarInput = (stats.operationalSolarPurchased || 0) + (stats.arbitrageSolarPurchased || 0);
    const totalFactoryWasteHeat = stats.wasteHeatAccepted || 0;
    const totalInput = totalGridInput + totalSolarInput + totalFactoryWasteHeat;
    const totalOutput = stats.netDelivery || 0;
    
    const efficiency = totalInput > 0 ? ((totalOutput / totalInput) * 100).toFixed(1) : 0;
    
    const option = {
      title: {
        text: `Energy Flow Analysis - Hours 0-${currentHour}`,
        subtext: `An-værk Produktion: ${Math.round(anVaerkProduction)} MWh | Heater Input: ${Math.round(heaterInput)} MWh | Overskudsvarme: ${Math.round(totalFactoryWasteHeat)} MWh | Netlevering: ${Math.round(totalOutput)} MWh | Efficiency: ${efficiency}%`,
        left: 'center',
        top: 5,
        textStyle: { fontSize: 14, fontWeight: 'bold' },
        subtextStyle: { fontSize: 11 }
      },
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove',
        formatter: function(params) {
          if (params.dataType === 'edge') {
            const source = params.data.source;
            const target = params.data.target;
            const value = params.value;
            
            let extra = '';
            // Add cost information for purchases
            if (source === 'Grid Electricity') {
              const cost = value * (stats.avgGridPrice || 400);
              extra = `<br/>Est. Cost: ${Math.round(cost).toLocaleString()} DKK`;
            } else if (source === 'Solar Energy') {
              const cost = value * (stats.avgSolarPrice || 350);
              extra = `<br/>Est. Cost: ${Math.round(cost).toLocaleString()} DKK`;
            } else if (source === 'Factory Waste Heat') {
              const cost = value * 150; // Fixed price for factory waste heat
              extra = `<br/>Cost: ${Math.round(cost).toLocaleString()} DKK`;
            } else if (target === 'Grid Sales') {
              const revenue = value * 600;
              extra = `<br/>Est. Revenue: ${Math.round(revenue).toLocaleString()} DKK`;
            }
            
            return `<b>${source} → ${target}</b><br/>` +
                   `Energy: ${value.toFixed(2)} MWh${extra}`;
          }
          return params.name;
        }
      },
      series: [{
        type: 'sankey',
        layout: 'none',
        emphasis: {
          focus: 'adjacency',
          blurScope: 'global'
        },
        lineStyle: {
          curveness: 0.5,
          opacity: 0.5
        },
        label: {
          show: true,
          fontSize: 10,
          color: '#333'
        },
        itemStyle: {
          borderWidth: 1,
          borderColor: '#aaa'
        },
        data: nodes,
        links: links,
        left: '5%',
        right: '15%',
        top: 60,
        bottom: 10,
        nodeWidth: 15,
        nodeGap: 12,
        layoutIterations: 32,
        focusNodeAdjacency: 'allEdges',
        draggable: true
      }]
    };
    
    chartInstance.current.setOption(option);
  }, [hourlyData, currentHour, stats, cumulativeFlows, config.an_plant_loss, config.ab_plant_loss, config.htes_modules, config.ltes_capacity]);
  
  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return (
    <div 
      ref={chartRef} 
      style={{ 
        width: '100%', 
        height: '500px',
        border: '1px solid #ddd',
        borderRadius: '4px',
        backgroundColor: '#fafafa'
      }} 
    />
  );
};

export default SankeyDiagramSimple;